// components/dashboard/MacroSummary.tsx
// Last macro-pass card: run id, ts, ESO mode, regions count, EP mean.

import { Link } from "react-router-dom";
import type { V38DashboardSnapshot } from "../../lib/api";
import { useIsController } from "../RequireAdmin";
import { labelFor } from "../../lib/labels";

export interface MacroSummaryProps {
  macro: V38DashboardSnapshot["macro"];
}

export default function MacroSummary({ macro }: MacroSummaryProps) {
  const admin = useIsController();   // #145 -- the door is the admin's
  return (
    <section style={panelStyle}>
      <header style={headerStyle}>
        <h2 style={{ margin: 0, fontSize: 16 }}>Macro-ELINS</h2>
        {admin ? <Link to="/founder" style={mutedLinkStyle}>open macro view →</Link> : null}
      </header>
      {macro.last_run_id ? (
        <div>
          <Row label="Last run" value={macro.last_run_id} mono />
          <Row label="Timestamp" value={fmtTs(macro.last_run_ts)} mono />
          {/* #180a (i) -- the literal carries its instrument and the snapshot key. */}
          <Row k="ep_mean" path="snapshot.macro.ep_mean" value={macro.ep_mean !== null ? macro.ep_mean.toFixed(3) : "—"} mono />
          <Row label="Regions" value={String(macro.regions_count ?? "—")} mono />
          <Row label="ESO mode" value={macro.external_signal_mode || "—"} />
        </div>
      ) : (
        <div style={{ fontSize: 12, color: "var(--os-text-tertiary, #585858)" }}>
          No macro runs yet
        </div>
      )}
    </section>
  );
}

function fmtTs(ts: number | null): string {
  if (!ts || ts <= 0) return "—";
  return new Date(ts * 1000).toISOString().replace("T", " ").slice(0, 19);
}

function Row({
  label, k, path, value, mono,
}: {
  label?: string;
  /** A labels.ts key: the word + instrument replace `label`, `path` rides in the title. */
  k?: string;
  path?: string;
  value: string;
  mono?: boolean;
}) {
  const l = k ? labelFor(k) : null;
  const text = l ? `${l.word}${l.instrument ? ` · ${l.instrument}` : ""}` : (label ?? "");
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", fontSize: 12 }}>
      <span style={{ color: "var(--os-text-secondary, #A0A0A0)" }} title={path}>{text}</span>
      <span style={{ fontFamily: mono ? "var(--font-mono, monospace)" : undefined }}>{value}</span>
    </div>
  );
}

const panelStyle: React.CSSProperties = {
  border: "1px solid var(--os-line-strong, rgba(20, 24, 28, 0.12))",
  borderRadius: "var(--radius-md, 8px)",
  padding: 12,
  background: "var(--os-surface, #111)",
  color: "var(--os-text-primary, #fff)",
};
const headerStyle: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8,
};
const mutedLinkStyle: React.CSSProperties = {
  fontSize: 11, color: "var(--os-focus, #00F0FF)", textDecoration: "none",
};
